import { DateTime } from 'luxon';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createGlobalState, useInterval, usePrevious } from 'react-use';

import { authSelectors } from 'app-components/flux/auth';
import { getEnvironmentConfig, tracker } from 'app-components/utils/core';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import DialogContent from '@mui/material/DialogContent';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { paymentMethodsActions, paymentMethodsSelectors } from 'app-components/flux/payment-methods';
import { preferencesActions, preferencesSelectors } from 'app-components/flux/preferences';
import { subscriptionsActions, subscriptionsSelectors } from 'app-components/flux/subscriptions';
import store from 'app-components/utils/redux-store';

import Zoom from '@mui/material/Zoom';
import { useFetchOnce } from 'app-components/hooks/utils';
import simplifiLogo from 'assets/logos/simplifi-logo.svg';
import SwitchPlan from 'assets/marketing/switch-plan.svg';
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import ZeroStateView from 'components/ZeroStateView';

const useGlobalOpenPopup = createGlobalState();
const useGlobalOpenDrawer = createGlobalState();
const useGlobalOpenConversionPopup = createGlobalState();

export const useGlobalSubscriptionMessagingMock = createGlobalState();

const useStyles = makeStyles()((theme) => ({
  daysToExpireChip: {
    marginLeft: 14,
    marginBottom: 8,
    color: theme.palette.warning.dark,
    backgroundColor: theme.palette.warning.light,
  },
}));

const messagesMap = {
  [subscriptionsSelectors.subscriptionMessages.MESSAGE1_1_ESTORE_TRIAL_CANCEL]: 'estore trial',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE1_2_MOBILE_TRIAL_CANCEL]: 'mobile triall',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL]: 'estore monthly paid',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL]: 'mobile monthly paid',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL]: 'estore annual paid',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL]: 'mobile annual paid paid',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE4_ESTORE_NO_PAYMENT_TOKEN]: 'estore no payment token',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD]: 'estore pre auth failed',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE6_ESTORE_GRACE_PERIOD]: 'estore grace period',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE7_MOBILE_GRACE_PERIOD]: 'mobile grace period',
  [subscriptionsSelectors.subscriptionMessages.MESSAGE9_CONVERT_MONTHLY_TO_YEARLY]: 'monthly to annual',
};

const SubscriptionMessaging = (_props) => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const theme = useTheme();

  useFetchOnce(subscriptionsActions.getSubscriptions, subscriptionsSelectors, false, true);
  useFetchOnce(paymentMethodsActions.getPaymentMethods, paymentMethodsSelectors, false, true);
  useEffect(() => {
    dispatch(preferencesActions.getPreferencesUser());
  }, [dispatch]);

  const [today, setToday] = useState(DateTime.utc().startOf('day'));
  const [subscriptionMessageMock] = useGlobalSubscriptionMessagingMock();
  const subscriptionMessageReal = useSelector((state) =>
    subscriptionsSelectors.getSubscriptionMessage(state, today.toISODate()),
  );
  const subscriptionMessage = subscriptionMessageMock || subscriptionMessageReal;

  const subscription = useSelector(subscriptionsSelectors.getActiveSubscription);
  const daysToExpire = useMemo(() => {
    const daysLeft = DateTime.fromISO(subscription?.expireOn, { zone: 'utc' }).diff(today).as('days') + 1;
    return Number.isNaN(Number(daysLeft)) ? '...' : daysLeft;
  }, [subscription, today]);

  const preferencesLoadPending = useSelector(preferencesSelectors.getLoadPending);
  const popupMessageDismissedOnISO = useSelector((state) =>
    preferencesSelectors.getSharedPreferencesByPath(state, { group: 'user' }),
  )?.subscriptionMessageDismissedOn;
  // const popupMessageDismissedOnISO = '2020-01-01';
  const popupMessageDismissedOn = useMemo(
    () => popupMessageDismissedOnISO && DateTime.fromISO(popupMessageDismissedOnISO, { zone: 'utc' }),
    [popupMessageDismissedOnISO],
  );

  const [messageContent, setMessageContent] = useState({});
  const [openPopup, setOpenPopup] = useGlobalOpenPopup();
  const openPopupPrev = usePrevious(openPopup);
  const [openDrawer, setOpenDrawer] = useGlobalOpenDrawer();
  const openDrawerPrev = usePrevious(openDrawer);
  const [openConversionPopup, setOpenConversionPopup] = useGlobalOpenConversionPopup();
  const openConversionPopupPrev = usePrevious(openConversionPopup);

  const monthlyAnnualAlert = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'monthlyAnnualAlert'));
  const subscriptionAlerts = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'subscriptionAlerts'));

  useEffect(() => {
    if ((!openPopupPrev && openPopup) || (!openConversionPopupPrev && openConversionPopup)) {
      tracker.track(tracker.events.subscriptionAlertShown, {
        type: 'modal',
        req: messagesMap[subscriptionMessage] || subscriptionMessage,
        days_until_expiration: daysToExpire,
      });
    } else if ((openPopupPrev && !openPopup) || (openConversionPopupPrev && !openConversionPopup)) {
      tracker.track(tracker.events.subscriptionAlertDismissed, {
        type: 'modal',
        req: messagesMap[subscriptionMessage] || subscriptionMessage,
        days_until_expiration: daysToExpire,
      });
    }
  }, [openPopup, openPopupPrev, openConversionPopup, openConversionPopupPrev, daysToExpire, subscriptionMessage]);

  useEffect(() => {
    if (!openDrawerPrev && openDrawer) {
      tracker.track(tracker.events.subscriptionAlertShown, {
        type: 'snackbar',
        req: messagesMap[subscriptionMessage] || subscriptionMessage,
        days_until_expiration: daysToExpire,
      });
    } else if (openDrawerPrev && !openDrawer) {
      tracker.track(tracker.events.subscriptionAlertDismissed, {
        type: 'snackbar',
        req: messagesMap[subscriptionMessage] || subscriptionMessage,
        days_until_expiration: daysToExpire,
      });
    }
  }, [openDrawer, openDrawerPrev, daysToExpire, subscriptionMessage]);

  const dismissMessage = useCallback(() => {
    setOpenPopup(false);
    setOpenDrawer(false);
    dispatch(
      preferencesActions.setPreference({
        section: 'shared',
        group: 'user',
        preference: { subscriptionMessageDismissedOn: DateTime.utc().startOf('day').toISODate() },
      }),
    );
  }, [setOpenPopup, setOpenDrawer, dispatch]);

  const continueSubscription = useCallback(() => {
    dismissMessage();
    setTimeout(() => {
      const authSession = authSelectors.getAuthSession(store.getState());
      window.location.href = `${getEnvironmentConfig().myaccount_url}?site=us&utm_medium=alert_transact&bearer=${
        authSession?.accessToken
      }`;
    }, 1000);
  }, [dismissMessage]);

  const updatePaymentInfo = useCallback(() => {
    dismissMessage();
    setTimeout(() => {
      const authSession = authSelectors.getAuthSession(store.getState());
      window.location.href = `${
        getEnvironmentConfig().myaccount_url
      }/update-payment?site=us&product_line=acme&utm_medium=alert_transact&bearer=${authSession?.accessToken}`;
    }, 1000);
  }, [dismissMessage]);

  const changePlan = useCallback(() => {
    setOpenConversionPopup(false);
    dispatch(
      preferencesActions.setPreference({
        section: 'shared',
        group: 'user',
        preference: { annualConversionMessageDismissedOn: DateTime.utc().startOf('day').toISODate() },
      }),
    );
    dispatch(
      subscriptionsActions.updateSubscriptionRenewalFrequency({
        productLineUriName: 'acme',
        renewalFrequency: 'P1Y',
      }),
    );
  }, [dispatch, setOpenConversionPopup]);

  useEffect(() => {
    if (subscriptionMessage !== subscriptionsSelectors.subscriptionMessages.NO_MESSAGE && !preferencesLoadPending) {
      switch (subscriptionMessage) {
        case subscriptionsSelectors.subscriptionMessages.MESSAGE1_1_ESTORE_TRIAL_CANCEL:
          setMessageContent({
            primary: 'Uh oh!',
            secondary: `Your Simplifi trial expires in ${daysToExpire} days. Visit My Account to continue your subscription.`,
            actionLabel: 'Continue subscription',
            daysToExpire: `${daysToExpire} days left`,
            onClick: continueSubscription,
          });
          break;
        case subscriptionsSelectors.subscriptionMessages.MESSAGE2_1_ESTORE_MONTHLY_PAID_CANCEL:
        case subscriptionsSelectors.subscriptionMessages.MESSAGE3_1_ESTORE_ANNUAL_PAID_CANCEL:
          setMessageContent({
            primary: 'Uh oh!',
            secondary: `Your Simplifi subscription expires in ${daysToExpire} days. Visit My Account to continue your subscription.`,
            actionLabel: 'Continue subscription',
            daysToExpire: `${daysToExpire} days left`,
            onClick: continueSubscription,
          });
          break;
        case subscriptionsSelectors.subscriptionMessages.MESSAGE1_2_MOBILE_TRIAL_CANCEL:
          setMessageContent({
            primary: 'Uh oh!',
            secondary: `Your Simplifi trial expires in ${daysToExpire} days. Open the Simplifi mobile app to continue your subscription.`,
            daysToExpire: `${daysToExpire} days left`,
          });
          break;
        case subscriptionsSelectors.subscriptionMessages.MESSAGE2_2_MOBILE_MONTHLY_PAID_CANCEL:
        case subscriptionsSelectors.subscriptionMessages.MESSAGE3_2_MOBILE_ANNUAL_PAID_CANCEL:
          setMessageContent({
            primary: 'Uh oh!',
            secondary: `Your Simplifi subscription expires in ${daysToExpire} days. Open the Simplifi mobile app to continue your subscription.`,
            daysToExpire: `${daysToExpire} days left`,
          });
          break;
        case subscriptionsSelectors.subscriptionMessages.MESSAGE4_ESTORE_NO_PAYMENT_TOKEN:
          setMessageContent({
            primary: 'Oops!',
            secondary: `We don't seem to have your most recent credit card details. Update your account within ${daysToExpire} days to continue using Simplifi.`,
            actionLabel: 'Update payment info',
            daysToExpire: `${daysToExpire} days left`,
            onClick: updatePaymentInfo,
          });
          break;
        case subscriptionsSelectors.subscriptionMessages.MESSAGE5_ESTORE_INVALID_PAYMENT_METHOD:
          setMessageContent({
            primary: 'Oops!',
            secondary: `Your payment wasn’t processed. Update your account within ${daysToExpire} days to continue using Simplifi.`,
            actionLabel: 'Update payment info',
            daysToExpire: `${daysToExpire} days left`,
            onClick: updatePaymentInfo,
          });
          break;
        case subscriptionsSelectors.subscriptionMessages.MESSAGE6_ESTORE_GRACE_PERIOD:
        case subscriptionsSelectors.subscriptionMessages.MESSAGE7_MOBILE_GRACE_PERIOD:
          setMessageContent({
            primary: 'Oops!',
            secondary: 'Your payment wasn’t processed. Update your account to continue using Simplifi.',
            actionLabel: 'Update payment info',
            onClick: updatePaymentInfo,
          });
          break;
        case subscriptionsSelectors.subscriptionMessages.MESSAGE9_CONVERT_MONTHLY_TO_YEARLY:
          if (monthlyAnnualAlert) {
            setMessageContent({
              secondary: 'Thanks for being a loyal Simplifi customer. Switch to an annual plan and save!',
              actionLabel: 'Switch to annual',
              onClick: changePlan,
            });
            setOpenConversionPopup(true);
          }
          return;
        default:
          assert(false, `unhandled message = '${subscriptionMessage}'`);
      }
      if (subscriptionAlerts) {
        if (!popupMessageDismissedOn || today.diff(popupMessageDismissedOn).as('days') > 30) {
          setOpenPopup(openPopup ?? true);
          setOpenDrawer(false);
        } else {
          setOpenDrawer(openDrawer ?? true);
          setOpenPopup(false);
        }
      }
    }
  }, [
    subscriptionMessage,
    today,
    preferencesLoadPending,
    popupMessageDismissedOn,
    daysToExpire,
    openPopup,
    setOpenPopup,
    openDrawer,
    setOpenDrawer,
    setMessageContent,
    updatePaymentInfo,
    continueSubscription,
    changePlan,
    setOpenConversionPopup,
    monthlyAnnualAlert,
    subscriptionAlerts,
  ]);

  useInterval(
    () => {
      const now = DateTime.utc().startOf('day');
      if (!today.equals(now)) {
        setToday(now);
        setOpenDrawer(undefined); // show drawer again if necessary
      }
    },
    1000 * 60 * 60,
  ); // once per hour

  return (
    <>
      <StdDialog
        colorBar={theme.palette.background.default}
        onClose={() => {
          setOpenPopup(false);
          setOpenDrawer(true);
          dispatch(
            preferencesActions.setPreference({
              section: 'shared',
              group: 'user',
              preference: { subscriptionMessageDismissedOn: DateTime.utc().startOf('day').toISODate() },
            }),
          );
        }}
        open={Boolean(openPopup)}
        size='xs'
        transitionDuration={1000}
      >
        <DialogContent>
          <ZeroStateView
            qIllustration='calendarClock'
            title={messageContent.primary}
            subtitle={messageContent.daysToExpire}
            description={messageContent.secondary}
            button={
              messageContent.actionLabel && (
                <Button
                  variant='contained'
                  onClick={(event) => {
                    messageContent?.onClick?.(event);
                    tracker.track(tracker.events.subscriptionAlertCTA, {
                      type: 'modal',
                      req: messagesMap[subscriptionMessage] || subscriptionMessage,
                      days_until_expiration: daysToExpire,
                    });
                  }}
                >
                  {messageContent.actionLabel || ''}
                </Button>
              )
            }
          />
        </DialogContent>
      </StdDialog>

      <Drawer
        anchor='bottom'
        variant='persistent'
        open={Boolean(openDrawer)}
        onClose={() => setOpenDrawer(false)}
        transitionDuration={1000}
        PaperProps={{ sx: { backgroundColor: theme.palette.background.paper } }}
      >
        <Box p={3} width='100%' display='flex'>
          <Box p={3} />

          <Box position='absolute' top={0} right={0}>
            <IconButton onClick={() => setOpenDrawer(false)} size='large'>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box marginLeft={1} marginRight={1}>
            <img src={simplifiLogo} width={40} height={40} alt='Quicken Simplifi' />
          </Box>

          <div>
            {Boolean(messageContent.daysToExpire) && (
              <Chip label={messageContent.daysToExpire} className={classes.daysToExpireChip} />
            )}

            <Typography style={{ marginLeft: 14 }}>
              {messageContent.primary}&nbsp;{messageContent.secondary}
            </Typography>

            {messageContent.actionLabel && (
              <Zoom in style={{ transitionDelay: '1000ms' }}>
                <QButton
                  onClick={(event) => {
                    messageContent?.onClick?.(event);
                    tracker.track(tracker.events.subscriptionAlertCTA, {
                      type: 'snackbar',
                      req: messagesMap[subscriptionMessage] || subscriptionMessage,
                      days_until_expiration: daysToExpire,
                    });
                  }}
                >
                  {messageContent.actionLabel || ''}
                </QButton>
              </Zoom>
            )}
          </div>
        </Box>
      </Drawer>

      <StdDialog
        colorBar={theme.palette.background.default}
        onClose={() => {
          setOpenConversionPopup(false);
          dispatch(
            preferencesActions.setPreference({
              section: 'shared',
              group: 'user',
              preference: { annualConversionMessageDismissedOn: DateTime.utc().startOf('day').toISODate() },
            }),
          );
        }}
        open={Boolean(openConversionPopup)}
        size='xs'
        transitionDuration={1000}
      >
        <DialogContent>
          <Box
            display='flex'
            flexDirection='column'
            // justifyContent="center"
            alignItems='center'
          >
            <Typography variant='bodyRegular' align='center'>
              Thanks for being a loyal Simplifi customer.
              <br />
              Switch to an annual plan and save!
            </Typography>

            <Zoom in style={{ transitionDelay: '1000ms' }}>
              <ButtonBase
                onClick={(event) => {
                  messageContent?.onClick?.(event);
                  tracker.track(tracker.events.subscriptionAlertCTA, {
                    type: 'modal',
                    req: messagesMap[subscriptionMessage] || subscriptionMessage,
                    days_until_expiration: daysToExpire,
                  });
                }}
              >
                <Box m={3}>
                  <img alt='switch plan' src={SwitchPlan} />
                </Box>
              </ButtonBase>
            </Zoom>

            <Typography variant='captionRegular'>*your current plan is $3.99 per month</Typography>
          </Box>
        </DialogContent>
      </StdDialog>
    </>
  );
};

export default SubscriptionMessaging;
